const { getElement, debounce, formatNumberWithCommas } = window.BAO.Utils

class RangeSlider extends window.BAO.CustomElement() {
  static get requiredAttributes () {
    return ['min', 'max']
  }

  static get requiredElements () {
    return ['touchLeft', 'touchRight', 'line']
  }

  constructor () {
    super()

    switch (this.dataset.type) {
      case 'search':
        this.eventBus = window.theme.searchResults.eventBus
        this.state = {
          root: window.theme.searchResults?.stateLayer?.state || {
            products: [],
          },
          observer: null,
        }

        this.type = 'klevu'
        break
      case 'autocomplete':
        this.eventBus = window.theme.autocompleteResults.eventBus
        this.state = {
          root: window.theme.autocompleteResults?.stateLayer?.state || {
            products: [],
          },
          observer: null,
        }

        this.type = 'klevu'
        break
      case 'shopify':
        this.type = 'shopify'
        break
      default:
        // Default is collection
        this.eventBus = window.theme.category.eventBus
        this.state = {
          root: window.theme.category?.stateLayer?.state || { products: [] },
          observer: null,
        }

        this.type = 'klevu'
    }
  }

  connectedCallback () {
    super.connectedCallback()

    this.startX = 0
    this.x = 0
    this.selectedTouchEl = null

    this.setTouchElPositions()
  }

  setupListeners () {
    super.setupListeners()

    this.onStart = this.onStart.bind(this)
    this.onStop = this.onStop.bind(this)
    this.onMove = this.onMove.bind(this)
    this.setTouchElPositions = this.setTouchElPositions.bind(this)

    this.listeners.add(this.touchLeftEl, 'mousedown', this.onStart)
    this.listeners.add(this.touchRightEl, 'mousedown', this.onStart)
    this.listeners.add(this.touchLeftEl, 'touchstart', this.onStart)
    this.listeners.add(this.touchRightEl, 'touchstart', this.onStart)

    this.onResize = this.onResize.bind(this)
    this.debouncedResize = debounce(event => {
      this.onResize(event)
    }, 240)
    this.listeners.add(window, 'resize', this.debouncedResize)

    const detailsEl = this.closest('details')
    if (detailsEl) {
      detailsEl.addEventListener('toggle', event => {
        if (event.target.open) {
          setTimeout(this.setTouchElPositions, 100)
        }
      })
    }

    if (this.type === 'klevu') {
      this.eventBus.subscribe('state:resetPriceRange', () => {
        this.minValue = this.min
        this.maxValue = this.max
        this.setTouchElPositions()
      })

      this.eventBus.subscribe(
        'price-range:change',
        ({ minValueNotExchanged, maxVisual }) => {
          this.minValue = minValueNotExchanged
          this.maxValue = maxVisual

          this.setTouchElPositions()
        },
      )
    }
  }

  onResize () {
    this.setTouchElPositions()
  }

  onStart (event) {
    if (event?.button && event?.button !== 0) return

    // Prevent default dragging of selected content
    event.preventDefault()
    this.eventTouch = event.touches ? event.touches[0] : event

    this.selectedTouchEl =
      event.target.closest(this.els.touchLeft.selector) ||
      event.target.closest(this.els.touchRight.selector)

    this.x = this.selectedTouchEl.matches(this.els.touchLeft.selector)
      ? this.touchLeftEl.offsetLeft
      : this.touchRightEl.offsetLeft
    this.startX = this.eventTouch.pageX - this.x

    document.addEventListener('mousemove', this.onMove)
    document.addEventListener('mouseup', this.onStop)
    document.addEventListener('touchmove', this.onMove)
    document.addEventListener('touchend', this.onStop)
  }

  onStop () {
    document.removeEventListener('mousemove', this.onMove)
    document.removeEventListener('mouseup', this.onStop)
    document.removeEventListener('touchmove', this.onMove)
    document.removeEventListener('touchend', this.onStop)

    const changeEvent = new Event('change', {
      bubbles: true,
      cancelable: true,
    })

    if (
      this.minToUpdateEl &&
      this.selectedTouchEl.matches(this.els.touchLeft.selector)
    ) {
      this.minToUpdateEl.dispatchEvent(changeEvent)
    }

    if (
      this.maxToUpdateEl &&
      this.selectedTouchEl.matches(this.els.touchRight.selector)
    ) {
      this.maxToUpdateEl.dispatchEvent(changeEvent)
    }

    this.selectedTouchEl = null
  }

  onMove (event) {
    this.eventTouch = event.touches ? event.touches[0] : event

    this.x = this.eventTouch.pageX - this.startX

    if (this.selectedTouchEl.matches(this.els.touchLeft.selector)) {
      if (this.x > this.touchRightEl.offsetLeft) {
        this.x = this.touchRightEl.offsetLeft
      } else if (this.x < 0) {
        this.x = 0
      }

      this.selectedTouchEl.style.left = `${this.x}px`

      const range = this.max - this.min
      const calculatedValue =
        this.min + Math.floor((range * this.percentage) / 10) * 10
      this.minValue = Math.max(this.min, Math.min(calculatedValue, this.max))
      this.setHighlighterPosition(this.x, null)
    }

    if (this.selectedTouchEl.matches(this.els.touchRight.selector)) {
      if (this.x < this.touchLeftEl.offsetLeft) {
        this.x = this.touchLeftEl.offsetLeft
      } else if (this.x > this.maxX) {
        this.x = this.maxX
      }
      this.selectedTouchEl.style.left = `${this.x}px`

      const range = this.max - this.min
      const calculatedValue =
        this.min + Math.ceil((range * this.percentage) / 10) * 10
      this.maxValue = Math.max(this.min, Math.min(calculatedValue, this.max))
      this.setHighlighterPosition(
        null,
        this.els.line.element.offsetWidth - this.x,
      )
    }
  }

  setTouchElPositions () {
    const range = this.max - this.min
    const leftX =
      ((this.minValue - this.min) / range) * this.els.line.element.offsetWidth
    const rightX =
      ((this.maxValue - this.min) / range) * this.els.line.element.offsetWidth

    this.touchLeftEl.style.left = `${leftX}px`
    this.touchRightEl.style.left = `${rightX}px`
    this.touchRightEl.style.right = 'unset'

    let highlightPosition = this.els.line.element.offsetWidth - rightX

    if (this.els.line.element.offsetWidth === rightX) {
      highlightPosition = `0`
    }

    this.setHighlighterPosition(leftX, highlightPosition)
  }

  setHighlighterPosition (leftX, rightX) {
    if (this.els?.highlighter?.exists) {
      if (leftX !== null) {
        this.els.highlighter.element.style.left = `${leftX}px`
      }
      if (rightX !== null) {
        this.els.highlighter.element.style.right = `${rightX}px`
      }
    }
  }

  get percentage () {
    return this.x / this.els.line.element.offsetWidth
  }

  get min () {
    return this.hasAttribute('min') ? parseFloat(this.getAttribute('min')) : 0
  }

  get max () {
    return this.hasAttribute('max') ? parseFloat(this.getAttribute('max')) : 100
  }

  get maxX () {
    return this.offsetWidth - this.touchRightEl.offsetWidth * 2
  }

  set minValue (value) {
    this.setAttribute('min-value', value)

    if (this.minToUpdateEl) {
      const inputMin = Number(this.minToUpdateEl.dataset.rangeMin)
      if (Number(value) < inputMin) {
        this.minToUpdateEl.value = inputMin
      } else {
        this.minToUpdateEl.value = value
      }

      this.minToUpdateEl.dispatchEvent(new Event('input', { bubbles: true }))
    }

    this._minValue = value
  }

  get minValue () {
    if (!this._minValue) {
      return this.hasAttribute('min-value')
        ? parseFloat(this.getAttribute('min-value'))
        : this.min
    }

    return this._minValue
  }

  set maxValue (value) {
    if (this.maxToUpdateEl) {
      const inputMax = Number(this.maxToUpdateEl.dataset.rangeMax)
      const inputMin = Number(this.minToUpdateEl.dataset.rangeMin)
      const currencySymbol = window.theme.moneyFormat.split('{')[0]
      let setValue = value

      if (
        Number(setValue) === inputMax &&
        Number(this.minToUpdateEl.value) === inputMin
      ) {
        this.maxToUpdateEl.value = ''
      } else {
        if (Number(value) > inputMax) {
          setValue = inputMax
          this.maxLabelToUpdateEl.dataset.text = `${currencySymbol}${formatNumberWithCommas(
            setValue,
          )}+`
        }
        this.maxToUpdateEl.value = setValue
      }
      this.maxToUpdateEl.dispatchEvent(new Event('input', { bubbles: true }))
    }

    this._maxValue = value
  }

  get maxValue () {
    if (!this._maxValue) {
      return this.hasAttribute('max-value')
        ? parseFloat(this.getAttribute('max-value'))
        : this.max
    }

    return this._maxValue
  }

  get defaultMinValue () {
    const tmpMin = this.minValue >= this.min ? this.minValue : this.min
    return tmpMin > this.defaultMaxValue ? this.defaultMaxValue : tmpMin
  }

  get defaultMaxValue () {
    return this.maxValue >= this.max ? this.maxValue : this.max
  }

  get touchLeftEl () {
    return this.els.touchLeft.element
  }

  get touchRightEl () {
    return this.els.touchRight.element
  }

  get minToUpdateEl () {
    if (
      !this.externalEls?.container?.exists ||
      !this.externalEls?.min?.exists
    ) {
      return
    }
    if (this.closest(this.externalEls.container.selector)) {
      const containerEl = this.closest(this.externalEls.container.selector)
      return getElement(this.externalEls.min.selector, { context: containerEl })
    }
  }

  get maxToUpdateEl () {
    if (
      !this.externalEls?.container?.exists ||
      !this.externalEls?.max?.exists
    ) {
      return
    }
    if (this.closest(this.externalEls.container.selector)) {
      const containerEl = this.closest(this.externalEls.container.selector)
      return getElement(this.externalEls.max.selector, { context: containerEl })
    }
  }

  get maxLabelToUpdateEl () {
    if (
      !this.externalEls?.container?.exists ||
      !this.externalEls?.maxLabel?.exists
    ) {
      return
    }
    if (this.closest(this.externalEls.container.selector)) {
      const containerEl = this.closest(this.externalEls.container.selector)
      return getElement(this.externalEls.maxLabel.selector, {
        context: containerEl,
      })
    }
  }
}

if (!customElements.get('range-slider')) {
  customElements.define('range-slider', RangeSlider)
}
